import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, inr } from "../lib/api";
import { ChevronRight, ChevronLeft, Trophy, Check, MessageCircle, ArrowRight, Star, X, MapPin, Calendar, Maximize, Layers, Phone, RotateCw } from "lucide-react";
import Pano360Viewer from "../components/Pano360Viewer";
import Tour360Viewer from "../components/Tour360Viewer";
import useDocumentTitle from "../lib/useDocumentTitle";

export default function PortfolioDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    setActive(0);
    api.get(`/portfolio/${id}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [id]);

  useDocumentTitle(
    data?.item ? `${data.item.client || "Project"} — ${data.item.category || "Portfolio"}` : null,
    data?.item ? `${data.item.category || "House"} design by HouseDesignWala™ — real 2D drawings and 3D elevation for ${data.item.client || "this project"}${data.item.state ? `, ${data.item.state}` : ""}.` : null
  );

  if (!data) return <div className="py-20 text-center text-gray-500">Loading…</div>;
  const { item: p, related } = data;
  const images = [p.image, ...(p.gallery || p.thumbs || []).filter(x => x !== p.image)].slice(0, 8);
  const media360 = p.media_360 || [];
  const tour360 = p.tour_360;
  const hasTour = tour360?.scenes?.length > 0;

  return (
    <div className="bg-[#f8f8f8]">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
          <nav className="text-xs text-gray-500 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap" data-testid="portfolio-detail-breadcrumb">
            <Link to="/" className="hover:text-[#F7941D]">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/portfolio" className="hover:text-[#F7941D]">Portfolio</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1a3c5e] font-semibold">{p.client}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Title */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            {p.achievement === "best" && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F7941D] text-white text-[10px] font-extrabold uppercase tracking-wider shadow mb-3">
                <Trophy className="w-3 h-3" /> Best Design Award
              </div>
            )}
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1a3c5e] leading-tight tracking-tight" data-testid="portfolio-detail-title">
              {p.client} — {p.style ? `${p.style[0].toUpperCase()}${p.style.slice(1)} ` : ""}{p.service} {p.floors} Floor{p.floors > 1 ? "s" : ""} Design — {p.city}, {p.state}
            </h1>
            <div className="text-xs text-gray-500 mt-2 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[#F7941D]" /> {p.city}, {p.state}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3 text-[#F7941D]" /> Designed {p.year}</span>
              <span className="flex items-center gap-1.5"><Layers className="w-3 h-3 text-[#F7941D]" /> {p.floors} Floors</span>
              <span className="flex items-center gap-1.5"><Maximize className="w-3 h-3 text-[#F7941D]" /> {p.plot_size}</span>
              <span className="flex items-center gap-1.5">★ <span className="font-bold text-emerald-600">{p.rating}.0</span> Client rating</span>
            </div>
          </div>
          <Link to={`/services/${p.package}`} data-testid="order-similar-btn">
            <button className="px-5 py-2.5 bg-[#F7941D] hover:bg-[#e0861a] text-white text-sm font-bold rounded-xl shadow-md flex items-center gap-2">
              Order Similar <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>

        {/* Gallery */}
        <div className="grid lg:grid-cols-12 gap-3 lg:gap-4">
          <div className="lg:col-span-9">
            <div className="relative aspect-[4/3] sm:aspect-[16/10] bg-black rounded-2xl overflow-hidden cursor-zoom-in" onClick={() => setLightbox(true)} data-testid="portfolio-main-image">
              <img src={images[active]} alt={p.client} className="w-full h-full object-cover" />
              <button onClick={(e) => { e.stopPropagation(); setActive((active - 1 + images.length) % images.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setActive((active + 1) % images.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur rounded px-2 py-1 text-[10px] font-bold text-[#1a3c5e] uppercase">
                {active + 1} / {images.length}
              </div>
            </div>
            <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-thin">
              {images.map((src, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`} className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition ${active === i ? "border-[#F7941D]" : "border-gray-200 opacity-70 hover:opacity-100"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>

          {/* Specs side card */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#F7941D]">Package Used</div>
                <Link to={`/services/${p.package}`} className="font-display text-xl font-extrabold text-[#1a3c5e] hover:text-[#F7941D] block uppercase">{p.package}</Link>
              </div>
              <div className="text-sm space-y-2.5 border-t border-gray-200 pt-4">
                <SpecItem label="Client" value={p.client} />
                <SpecItem label="Location" value={`${p.city}, ${p.state}`} />
                <SpecItem label="Plot Size" value={p.plot_size} />
                <SpecItem label="Built Area" value={p.built_area} />
                <SpecItem label="Floors" value={`${p.floors} (G+${parseInt(p.floors) - 1})`} />
                <SpecItem label="Style" value={p.style ? p.style[0].toUpperCase() + p.style.slice(1) : "—"} />
                <SpecItem label="Year" value={p.year} />
                <SpecItem label="Designer" value={p.designer} />
              </div>
              <Link to={`/services/${p.package}`}>
                <button className="w-full py-2.5 bg-[#F7941D] hover:bg-[#e0861a] text-white font-bold rounded-xl text-sm" data-testid="sidebar-order-btn">
                  Order this Package
                </button>
              </Link>
              <a href={`https://wa.me/919050785528?text=Hi, I love the ${p.client} project (${p.id}). Can I get a similar design?`} target="_blank" rel="noopener noreferrer">
                <button className="w-full py-2.5 border-2 border-[#25D366] text-[#075E54] hover:bg-[#25D366] hover:text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Ask on WhatsApp
                </button>
              </a>
            </div>
          </aside>
        </div>

        {/* Summary + features */}
        <div className="grid lg:grid-cols-12 gap-5 mt-8">
          <div className="lg:col-span-8 space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h2 className="font-display text-xl font-bold text-[#1a3c5e]">Project Overview</h2>
              <p className="text-sm text-gray-700 leading-relaxed mt-3">{p.summary}</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h2 className="font-display text-xl font-bold text-[#1a3c5e] mb-4">Key Features</h2>
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {(p.key_features || []).map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 360° Walkthrough Tour — multi-scene, hotspot navigation between rooms */}
            {hasTour && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm" data-testid="tour360-section">
                <div className="flex items-center gap-2 mb-3">
                  <RotateCw className="w-5 h-5 text-[#F7941D]" />
                  <h2 className="font-display text-xl font-bold text-[#1a3c5e]">Walk Through — 360° Tour</h2>
                </div>
                <p className="text-xs text-gray-500 mb-3">Drag to look around, click the arrows to move between rooms. Works on mobile too.</p>
                <Tour360Viewer tour={tour360} className="w-full h-[360px] sm:h-[480px]" />
              </div>
            )}

            {/* 360° Panoramas — only render if admin has uploaded any */}
            {media360.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm" data-testid="pano360-section">
                <div className="flex items-center gap-2 mb-3">
                  <RotateCw className="w-5 h-5 text-[#F7941D]" />
                  <h2 className="font-display text-xl font-bold text-[#1a3c5e]">Step Inside — 360° View</h2>
                </div>
                <p className="text-xs text-gray-500 mb-3">Drag, pinch and explore. Works on mobile too.</p>
                <div className="space-y-3">
                  {media360.map((src, i) => (
                    <Pano360Viewer
                      key={i}
                      src={src}
                      testId={`pano-${i}`}
                      className="w-full h-[320px] sm:h-[420px]"
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h2 className="font-display text-xl font-bold text-[#1a3c5e] mb-4">Specifications</h2>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-200">
                  <tr><td className="py-2.5 text-gray-500 w-1/3">Service</td><td className="py-2.5 font-medium text-[#1a3c5e]">{p.service}</td></tr>
                  <tr><td className="py-2.5 text-gray-500">Plot Dimensions</td><td className="py-2.5 font-medium text-[#1a3c5e]">{p.plot_size}</td></tr>
                  <tr><td className="py-2.5 text-gray-500">Built-up Area</td><td className="py-2.5 font-medium text-[#1a3c5e]">{p.built_area}</td></tr>
                  <tr><td className="py-2.5 text-gray-500">Floors</td><td className="py-2.5 font-medium text-[#1a3c5e]">{p.floors} ({p.floors === "1" ? "G" : `G+${parseInt(p.floors) - 1}`})</td></tr>
                  <tr><td className="py-2.5 text-gray-500">Architectural Style</td><td className="py-2.5 font-medium text-[#1a3c5e] capitalize">{p.style}</td></tr>
                  <tr><td className="py-2.5 text-gray-500">Project Status</td><td className="py-2.5 font-medium text-emerald-700">Delivered ✓</td></tr>
                  <tr><td className="py-2.5 text-gray-500">Package</td><td className="py-2.5 font-medium text-[#F7941D] uppercase">{p.package}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* CTA card */}
          <aside className="lg:col-span-4">
            <div className="bg-gradient-to-br from-[#FEF3E2] to-white border-2 border-[#F7941D]/30 rounded-2xl p-6 shadow-sm">
              <Trophy className="w-8 h-8 text-[#F7941D] mb-2" />
              <h3 className="font-display text-lg font-bold text-[#1a3c5e]">Want a design like {p.client.split(" ")[0]}'s?</h3>
              <p className="text-sm text-gray-600 mt-2">Order the same package and get a custom design tailored to your plot, family, and style.</p>
              <Link to={`/services/${p.package}`}>
                <button className="mt-4 w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] text-white font-bold rounded-xl shadow-md flex items-center justify-center gap-2">
                  Order {p.package?.charAt(0).toUpperCase() + p.package?.slice(1)} <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <div className="mt-4 pt-4 border-t border-[#F7941D]/20 text-xs space-y-1.5 text-gray-700">
                <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-[#F7941D]" /> +91 90507 82248</div>
                <div className="flex items-center gap-1.5"><Star className="w-3 h-3 text-[#F7941D] fill-[#F7941D]" /> 500+ projects delivered</div>
                <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-600" /> 100% online · Pan India</div>
              </div>
            </div>
          </aside>
        </div>

        {/* Related */}
        {related?.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-2xl font-bold text-[#1a3c5e] mb-5">Similar Projects</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="related-portfolio">
              {related.slice(0, 3).map(r => (
                <Link key={r.id} to={`/portfolio/${r.id}`} className="group" onClick={() => window.scrollTo(0, 0)}>
                  <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                    <img src={r.image} alt={r.client} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  </div>
                  <div className="mt-3">
                    <div className="font-display font-bold text-sm text-[#1a3c5e] group-hover:text-[#F7941D] leading-tight">{r.client} — {r.service}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{r.city}, {r.state}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-12">
          <button onClick={() => nav("/portfolio")} className="text-sm font-semibold text-[#F7941D] hover:underline">← Back to all portfolio</button>
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="portfolio-lightbox">
          <button className="absolute top-4 right-4 text-white p-2" onClick={() => setLightbox(false)}>
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-[16/10] bg-black rounded-xl overflow-hidden">
              <img src={images[active]} alt={p.client} className="w-full h-full object-contain" />
            </div>
            <div className="flex gap-2 mt-3 justify-center flex-wrap">
              {images.map((src, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-14 h-14 rounded-md overflow-hidden border-2 transition ${active === i ? "border-[#F7941D]" : "border-transparent opacity-50 hover:opacity-100"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SpecItem({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-[#1a3c5e] text-right">{value}</span>
    </div>
  );
}
